import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset } from '../api.ts';
import { LocalePicker } from '../components/LocalePicker.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { errorMessage } from '../messages.ts';

/**
 * La nuova password, dal collegamento ricevuto via email (mock 2, `st-forgot`).
 *
 * Stessa forma di `InvitePage`: il token sta nel frammento dell'indirizzo e
 * non lo scrive da nessuna parte (AK3), non fa entrare — finita la scelta si
 * torna all'accesso, dove chi ha il secondo fattore lo userà come sempre.
 *
 * A differenza dell'invito non c'è niente da **leggere** prima: un token di
 * reset non porta il nome di un ente né un ruolo da mostrare (sarebbe un
 * altro oracolo di esistenza), quindi qui non c'è una richiesta preliminare,
 * solo il modulo.
 */

type Props = {
  token: string;
  onDone: () => void;
};

export function PasswordResetPage({ token, onDone }: Props) {
  const { t } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const focusOnAppear = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus();
  }, []);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || password.length < 12) return;
    setBusy(true);
    try {
      await confirmPasswordReset({ token, password });
      setDone(true);
      say(t('password_reset.done'));
    } catch (error) {
      say(`${t('password_reset.failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main className="page page--narrow" id="main">
        <div className="card">
          <h1 ref={focusOnAppear} tabIndex={-1}>
            {t('password_reset.done_title')}
          </h1>
          {announcer}
          <p className="muted">{t('password_reset.done_hint')}</p>
          <button type="button" className="button button--primary" onClick={onDone}>
            {t('password_reset.go_to_login')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="page page--narrow" id="main">
      <div className="card">
        <h1 ref={focusOnAppear} tabIndex={-1}>
          {t('password_reset.title')}
        </h1>

        {announcer}

        <form onSubmit={(event) => void submit(event)} noValidate>
          <div className="field">
            <label htmlFor="reset-password">{t('password_reset.password_label')}</label>
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={200}
              value={password}
              aria-describedby="reset-password-hint"
              onChange={(event) => setPassword(event.target.value)}
            />
            <p className="muted" id="reset-password-hint">
              {t('password_reset.password_hint')}
            </p>
          </div>

          <button
            type="submit"
            className="button button--primary"
            disabled={busy || password.length < 12}
          >
            {busy ? t('password_reset.saving') : t('password_reset.submit')}
          </button>
        </form>
      </div>

      <div className="prefs-bar">
        <LocalePicker />
        <ThemePicker />
      </div>
    </main>
  );
}
